import type { SyncJob, SyncJobStageEvent, SyncStage } from '../types/mailbox';
import { isTerminalSyncStage } from '../types/mailbox';
import { getRecoverableGuidance, type ToneClass } from './mailboxStatusDisplay';

export { isTerminalSyncStage };

export const SYNC_STAGE_LABELS: Record<SyncStage, string> = {
    requested: 'Sync requested',
    authenticating: 'Checking Gmail access',
    metadata_fetch: 'Checking for new Gmail messages',
    email_insert: 'Saving emails',
    workitem_migration: 'Creating work items',
    enrichment: 'Organizing items',
    complete: 'Sync complete',
    failed: 'Sync needs attention',
};

const SYNC_STAGE_HELPER_TEXT: Record<SyncStage, string> = {
    requested: 'We accepted the sync request and are preparing this mailbox.',
    authenticating: 'We are verifying saved Gmail access for this mailbox.',
    metadata_fetch: 'We are looking for recent changes in this mailbox.',
    email_insert: 'We are saving mailbox messages safely.',
    workitem_migration: 'We are turning saved emails into work items.',
    enrichment: 'We are applying safe organization and enrichment.',
    complete: 'This mailbox finished syncing.',
    failed: 'Use the failure guidance for this mailbox.',
};

const UNKNOWN_STAGE_LABEL = 'Sync status unavailable';

export interface SyncStageDisplay {
    label: string;
    helperText: string;
    tone: ToneClass;
    isTerminal: boolean;
}

export type SyncFailurePrimaryAction = 'reconnect' | 'retry' | 'none';

export interface SyncFailureRecovery {
    headline: string;
    guidance: string;
    tone: ToneClass;
    primaryAction: SyncFailurePrimaryAction;
    /** Sanitized secondary detail when safe to show */
    secondaryDetail: string | null;
}

const RECONNECT_SYNC_ERROR_CLASSES = new Set([
    'SYNC_AUTHENTICATION_FAILED',
    'SYNC_TOKEN_INVALID',
    'SYNC_PROVIDER_LINKAGE_MISSING',
]);

const RETRY_SYNC_ERROR_CLASSES = new Set([
    'SYNC_RATE_LIMITED',
    'OAUTH_RATE_LIMITED',
    'SYNC_NETWORK',
    'OAUTH_NETWORK',
    'SYNC_LOCK_LOST',
    'SYNC_ORCHESTRATION_EXCEPTION',
    'SYNC_EXCEPTION',
    'SYNC_FAILED',
    'SYNC_PARTIAL_FAILURE',
]);

const RECONNECT_MAILBOX_ERROR_CLASSES = new Set([
    'oauth_expired',
    'connection_error',
    'connection_revoked',
]);

function isKnownSyncStage(stage: string): stage is SyncStage {
    return stage in SYNC_STAGE_LABELS;
}

function readMetricCount(metrics: Record<string, unknown> | undefined, key: string): number | null {
    if (!metrics) {
        return null;
    }
    const value = metrics[key];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return value;
    }
    return null;
}

function readLatestDetailCount(
    stageEvents: SyncJobStageEvent[] | undefined,
    stage: SyncStage,
    detailKey: string,
): number | null {
    if (!stageEvents?.length) {
        return null;
    }
    for (let index = stageEvents.length - 1; index >= 0; index -= 1) {
        const event = stageEvents[index];
        if (event.stage !== stage || !event.details) {
            continue;
        }
        const value = event.details[detailKey];
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            return value;
        }
    }
    return null;
}

export function formatSyncStageProgressSuffix(
    stage: SyncStage | string,
    metrics?: Record<string, unknown>,
    stageEvents?: SyncJobStageEvent[],
): string {
    if (stage === 'email_insert') {
        const count =
            readMetricCount(metrics, 'emails_inserted') ??
            readLatestDetailCount(stageEvents, 'email_insert', 'emails_inserted');
        return count != null ? ` (${count} saved)` : '';
    }
    if (stage === 'workitem_migration') {
        const count =
            readMetricCount(metrics, 'workitems_upserted') ??
            readLatestDetailCount(stageEvents, 'workitem_migration', 'workitems_upserted');
        return count != null ? ` (${count} created)` : '';
    }
    if (stage === 'enrichment') {
        const count =
            readMetricCount(metrics, 'ai_evaluations') ??
            readLatestDetailCount(stageEvents, 'enrichment', 'ai_evaluations');
        return count != null ? ` (${count} enriched)` : '';
    }
    return '';
}

function stageTone(stage: SyncStage): ToneClass {
    if (stage === 'complete') {
        return 'success';
    }
    if (stage === 'failed') {
        return 'danger';
    }
    if (stage === 'requested') {
        return 'neutral';
    }
    return 'warning';
}

export function getSyncStageDisplay(
    stage: SyncStage | string | undefined | null,
    job?: SyncJob | null,
): SyncStageDisplay {
    const resolvedStage = stage ?? job?.current_stage;
    if (!resolvedStage || !isKnownSyncStage(resolvedStage)) {
        if (typeof resolvedStage === 'string' && resolvedStage.length > 0) {
            console.warn(`Unknown sync stage: ${resolvedStage}`);
        }
        return {
            label: UNKNOWN_STAGE_LABEL,
            helperText: 'We could not read the current sync stage for this mailbox.',
            tone: 'muted',
            isTerminal: false,
        };
    }

    const suffix = formatSyncStageProgressSuffix(
        resolvedStage,
        job?.metrics,
        job?.stage_events,
    );

    return {
        label: `${SYNC_STAGE_LABELS[resolvedStage]}${suffix}`,
        helperText: SYNC_STAGE_HELPER_TEXT[resolvedStage],
        tone: stageTone(resolvedStage),
        isTerminal: isTerminalSyncStage(resolvedStage),
    };
}

function isSafeSecondaryDetail(message: string | null | undefined): string | null {
    const trimmed = (message || '').trim();
    if (!trimmed || trimmed.length > 160) {
        return null;
    }
    const lowered = trimmed.toLowerCase();
    if (
        lowered.includes('token') ||
        lowered.includes('bearer') ||
        lowered.includes('refresh_token') ||
        lowered.includes('authorization')
    ) {
        return null;
    }
    return trimmed;
}

export function getSyncFailureRecovery(
    syncJobErrorClass: string | null | undefined,
    mailboxErrorClass?: string | null,
    mailboxConnectionState?: string | null,
): SyncFailureRecovery {
    const syncCode = (syncJobErrorClass || '').trim();
    const mailboxCode = (mailboxErrorClass || '').trim();
    const connectionState = (mailboxConnectionState || '').toLowerCase();

    if (syncCode === 'SYNC_ALREADY_IN_PROGRESS') {
        return {
            headline: 'Sync already running',
            guidance: 'This mailbox already has a sync running. We will keep checking progress.',
            tone: 'warning',
            primaryAction: 'none',
            secondaryDetail: null,
        };
    }

    if (
        RECONNECT_SYNC_ERROR_CLASSES.has(syncCode) ||
        RECONNECT_MAILBOX_ERROR_CLASSES.has(mailboxCode) ||
        connectionState === 'error'
    ) {
        const mailboxGuidance = getRecoverableGuidance(mailboxCode);
        return {
            headline: mailboxGuidance?.headline ?? 'Gmail access needs attention',
            guidance:
                mailboxGuidance?.nextStep ??
                'Reconnect this mailbox to restore Gmail access.',
            tone: mailboxGuidance?.tone ?? 'warning',
            primaryAction: 'reconnect',
            secondaryDetail: null,
        };
    }

    if (RETRY_SYNC_ERROR_CLASSES.has(syncCode) || syncCode.startsWith('OAUTH_')) {
        if (syncCode === 'SYNC_RATE_LIMITED' || syncCode === 'OAUTH_RATE_LIMITED') {
            return {
                headline: 'Gmail is asking us to slow down',
                guidance: 'Gmail is asking us to slow down. Try again in a few minutes.',
                tone: 'warning',
                primaryAction: 'retry',
                secondaryDetail: null,
            };
        }
        if (syncCode === 'SYNC_NETWORK' || syncCode === 'OAUTH_NETWORK') {
            return {
                headline: 'Could not reach Gmail',
                guidance: 'We could not reach Gmail. Try syncing again.',
                tone: 'warning',
                primaryAction: 'retry',
                secondaryDetail: null,
            };
        }
        return {
            headline: 'Sync did not finish cleanly',
            guidance:
                'Some email work could not be organized. Try again; already saved items remain available.',
            tone: 'danger',
            primaryAction: 'retry',
            secondaryDetail: null,
        };
    }

    return {
        headline: 'Sync needs attention',
        guidance: 'We could not finish sync for this mailbox. Try syncing again.',
        tone: 'danger',
        primaryAction:
            connectionState === 'connected' && !mailboxCode ? 'retry' : 'retry',
        secondaryDetail: null,
    };
}

export function getSyncFailureRecoveryFromJob(
    job: SyncJob,
    mailboxErrorClass?: string | null,
    mailboxConnectionState?: string | null,
): SyncFailureRecovery {
    const recovery = getSyncFailureRecovery(
        job.error_class,
        mailboxErrorClass,
        mailboxConnectionState,
    );
    return {
        ...recovery,
        secondaryDetail: isSafeSecondaryDetail(job.error_message),
    };
}

export function shouldShowFailedJobCallout(
    job: SyncJob | null | undefined,
    mailbox: {
        connection_state?: string | null;
        error_class?: string | null;
        active_sync_job?: SyncJob | null;
        last_sync_job?: SyncJob | null;
    },
): boolean {
    if (!job || job.current_stage !== 'failed') {
        return false;
    }

    const active = mailbox.active_sync_job;
    if (active?.id === job.id) {
        return true;
    }

    if (active && !isTerminalSyncStage(active.current_stage)) {
        return false;
    }

    const connectionState = (mailbox.connection_state || '').toLowerCase();
    const isHealthyConnection = connectionState === 'connected' && !mailbox.error_class;

    if (mailbox.last_sync_job?.id === job.id && isHealthyConnection && !active) {
        return false;
    }

    return true;
}

export function formatMailboxScopedStageMessage(
    emailAddress: string,
    display: SyncStageDisplay,
): string {
    return `${display.label} for ${emailAddress}`;
}

export const RECENT_SLICE_READY_HINT =
    'Newest mail is ready — older mail still syncing';

/** Secondary line when recent slice finished but backfill may continue (Story 9.5). */
export function getRecentSliceHint(job?: SyncJob | null): string | null {
    if (!job?.metrics) {
        return null;
    }
    const metrics = job.metrics;
    if (metrics.recent_first_unsupported_reason) {
        return null;
    }
    if (isTerminalSyncStage(job.current_stage)) {
        return null;
    }
    if (job.current_stage === 'failed') {
        return null;
    }
    if (metrics.recent_slice_complete !== true) {
        return null;
    }
    const materialized = readMetricCount(metrics, 'recent_workitems_materialized');
    if (materialized == null || materialized <= 0) {
        return null;
    }
    if (metrics.backfill_in_progress !== true) {
        return null;
    }
    return RECENT_SLICE_READY_HINT;
}
